import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiUser, FiLock, FiBell, FiGlobe, FiInfo,
  FiShield, FiPhone, FiMail, FiClock,
  FiHelpCircle, FiCheck, FiEdit2, FiSave,
  FiX, FiAlertCircle, FiChevronRight,
} from "react-icons/fi";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import {
  getProfile,
  updateProfile,
  changePassword,
  getNotifications,
  markAllNotificationsRead,
  getAppInfo,
  API_URL,
} from "../../services/settings_service";
import "./Settings.css";

const resolvePhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}${path}`;
};

const readUserId = () => {
  const raw = localStorage.getItem("user_id");
  if (!raw) return null;
  const id = parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
};

export default function Settings() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(() => readUserId());
  const [authChecked, setAuthChecked] = useState(false);

  const [profile,       setProfile]       = useState(null);
  const [appInfo,       setAppInfo]       = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [successMsg,    setSuccessMsg]    = useState("");
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: "", email: "", mobile: "",
    designation: "", address: "", city: "", pincode: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [pwForm,   setPwForm]   = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError,  setPwError]  = useState("");
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  useEffect(() => {
    if (userId) {
      setAuthChecked(true);
      return;
    }

    const retryId = readUserId();
    if (retryId) {
      setUserId(retryId);
      setAuthChecked(true);
      return;
    }

    const timer = setTimeout(() => {
      const finalId = readUserId();
      if (finalId) {
        setUserId(finalId);
      } else {
        setError("User not logged in. Please login again.");
        setLoading(false);
      }
      setAuthChecked(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [userId]);

  useEffect(() => {
    if (!authChecked || !userId) return;
    loadAll(userId);
  }, [authChecked, userId]);

  const loadAll = async (id) => {
    try {
      setLoading(true);
      setError("");
      const [profileData, appData, notifData] = await Promise.all([
        getProfile(id),
        getAppInfo(),
        getNotifications(id),
      ]);
      setProfile(profileData);
      setAppInfo(appData);
      setNotifications(notifData);
      setProfileForm({
        full_name:   profileData.full_name   || "",
        email:       profileData.email       || "",
        mobile:      profileData.mobile      || "",
        designation: profileData.designation || "",
        address:     profileData.address     || "",
        city:        profileData.city        || "",
        pincode:     profileData.pincode     || "",
      });
    } catch (err) {
      console.error(err);
      setError("Failed to load settings. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleProfileSave = async () => {
    if (!userId) return;
    try {
      setProfileSaving(true);
      await updateProfile(userId, profileForm);
      setProfile((prev) => ({ ...prev, ...profileForm }));
      localStorage.setItem("full_name", profileForm.full_name);
      localStorage.setItem("mobile", profileForm.mobile);
      setEditingProfile(false);
      showSuccess("Profile updated successfully");
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileCancel = () => {
    setProfileForm({
      full_name:   profile.full_name   || "",
      email:       profile.email       || "",
      mobile:      profile.mobile      || "",
      designation: profile.designation || "",
      address:     profile.address     || "",
      city:        profile.city        || "",
      pincode:     profile.pincode     || "",
    });
    setEditingProfile(false);
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPwError("");
    if (!userId) return;
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError("New passwords do not match");
      return;
    }
    if (pwForm.new_password.length < 6) {
      setPwError("Password must be at least 6 characters");
      return;
    }
    try {
      setPwSaving(true);
      await changePassword(userId, pwForm.current_password, pwForm.new_password);
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      showSuccess("Password changed successfully");
    } catch (err) {
      setPwError(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setPwSaving(false);
    }
  };

  const handleMarkAllRead = async () => {
    if (!userId) return;
    try {
      await markAllNotificationsRead(userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      showSuccess("All notifications marked as read");
    } catch (err) {
      console.error(err);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading) {
    return (
      <DashboardShell>
        <div className="stg-loading">
          <div className="stg-spinner" />
          <p>Loading settings…</p>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      {successMsg && (
        <div className="stg-toast">
          <FiCheck size={14} /> {successMsg}
        </div>
      )}
      {error && (
        <div className="stg-error-banner">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}
      <div className="stg-hero">
        <div
          className="stg-avatar"
          onClick={() => profile?.photo_url && setShowPhotoModal(true)}
          style={{ cursor: profile?.photo_url ? "pointer" : "default" }}
          title={profile?.photo_url ? "Click to view full size" : undefined}
        >
          {profile?.photo_url ? (
            <img
              src={resolvePhotoUrl(profile.photo_url)}
              alt={profile.full_name}
              className="stg-avatar-img"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <span>{profile?.full_name?.charAt(0)?.toUpperCase() || "U"}</span>
          )}
        </div>
        <div className="stg-hero-info">
          <h2>{profile?.full_name || "—"}</h2>
          <p>
            {profile?.email || "—"}
            {profile?.role && (
              <> &nbsp;·&nbsp; <span className="stg-role-pill">{profile.role}</span></>
            )}
          </p>
          <p className="stg-last-login">
            <FiClock size={11} />
            Last login: {profile?.last_login || "—"}
          </p>
        </div>
        <div className="stg-hero-right">
          <span className={`stg-status-pill ${profile?.is_active ? "active" : "inactive"}`}>
            {profile?.is_active ? "Active" : "Inactive"}
          </span>
          {!editingProfile && (
            <button className="stg-edit-btn" onClick={() => setEditingProfile(true)}>
              <FiEdit2 size={13} /> Edit Profile
            </button>
          )}
        </div>
      </div>

      <div className="stg-grid">
        <div className="stg-card">
          <div className="stg-card-header">
            <FiUser size={15} />
            <h3>Account Details</h3>
          </div>

          {editingProfile ? (
            <div className="stg-form">
              <div className="stg-form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div className="stg-form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  placeholder="Email"
                />
              </div>
              <div className="stg-form-group">
                <label>Mobile</label>
                <input
                  type="text"
                  value={profileForm.mobile}
                  onChange={(e) => setProfileForm({ ...profileForm, mobile: e.target.value })}
                  placeholder="Mobile number"
                />
              </div>
              <div className="stg-form-group">
                <label>Designation</label>
                <input
                  type="text"
                  value={profileForm.designation}
                  onChange={(e) => setProfileForm({ ...profileForm, designation: e.target.value })}
                  placeholder="Designation"
                />
              </div>
              <div className="stg-form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={profileForm.address}
                  onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                  placeholder="Address"
                />
              </div>
              <div className="stg-form-group">
                <label>City</label>
                <input
                  type="text"
                  value={profileForm.city}
                  onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div className="stg-form-group">
                <label>Pincode</label>
                <input
                  type="text"
                  value={profileForm.pincode}
                  onChange={(e) => setProfileForm({ ...profileForm, pincode: e.target.value })}
                  placeholder="Pincode"
                />
              </div>
              <div className="stg-form-actions">
                <button className="stg-save-btn" onClick={handleProfileSave} disabled={profileSaving}>
                  <FiSave size={13} /> {profileSaving ? "Saving…" : "Save Changes"}
                </button>
                <button className="stg-cancel-btn" onClick={handleProfileCancel}>
                  <FiX size={13} /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="stg-info-grid">
              <InfoRow icon={FiUser}  label="Full Name"     value={profile?.full_name} />
              <InfoRow icon={FiMail}  label="Email Address" value={profile?.email}     />
              <InfoRow icon={FiPhone} label="Mobile"        value={profile?.mobile}    />
              <InfoRow icon={FiClock} label="Member Since"  value={profile?.created_at} />
              <InfoRow icon={FiUser}  label="Staff Code"    value={profile?.staff_code} />
              <InfoRow icon={FiUser}  label="Designation"   value={profile?.designation} />
              <InfoRow icon={FiGlobe} label="Branch"        value={profile?.branch_name} />
              <InfoRow icon={FiGlobe} label="Service Location" value={profile?.service_location} />
              <InfoRow icon={FiGlobe} label="Address"       value={profile?.address} />
              <InfoRow icon={FiGlobe} label="City"          value={profile?.city} />
              <InfoRow icon={FiGlobe} label="Pincode"       value={profile?.pincode} />
            </div>
          )}
        </div>

        {/* ── Change password ── */}
        <div className="stg-card">
          <div className="stg-card-header">
            <FiShield size={15} />
            <h3>Security</h3>
          </div>

          <form className="stg-form" onSubmit={handlePasswordSave}>
            {pwError && (
              <div className="stg-pw-error">
                <FiAlertCircle size={13} /> {pwError}
              </div>
            )}
            <div className="stg-form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                placeholder="Enter current password"
                required
              />
            </div>
            <div className="stg-form-group">
              <label>New Password</label>
              <input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                placeholder="Min. 6 characters"
                required
              />
            </div>
            <div className="stg-form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                placeholder="Repeat new password"
                required
              />
            </div>
            <div className="stg-form-actions">
              <button type="submit" className="stg-save-btn" disabled={pwSaving}>
                <FiLock size={13} /> {pwSaving ? "Changing…" : "Change Password"}
              </button>
            </div>
          </form>
        </div>
        <div className="stg-card">
          <div className="stg-card-header">
            <FiBell size={15} />
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="stg-notif-badge">{unreadCount}</span>
            )}
            {notifications.length > 0 && (
              <button className="stg-mark-all-btn" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="stg-notif-empty">
              <FiBell size={24} />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="stg-notif-list">
              {notifications.slice(0, 5).map((n) => (
                <div key={n.id} className={`stg-notif-item ${!n.is_read ? "unread" : ""}`}>
                  <div className="stg-notif-dot" />
                  <div className="stg-notif-body">
                    <p className="stg-notif-title">{n.title}</p>
                    <p className="stg-notif-msg">{n.message}</p>
                    <span className="stg-notif-time">{n.created_at}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="stg-card">
          <div className="stg-card-header">
            <FiInfo size={15} />
            <h3>General</h3>
          </div>
          <InfoRow icon={FiInfo}  label="Application" value={appInfo?.app_name}     />
          <InfoRow icon={FiInfo}  label="Version"     value={appInfo?.version}       badge={{ text: "Latest", type: "ok" }} />
          <InfoRow icon={FiPhone} label="Support"     value={appInfo?.support_phone} />
          <InfoRow icon={FiMail}  label="Email"       value={appInfo?.support_email} />
        </div>
        <div className="stg-card">
          <div className="stg-card-header">
            <FiHelpCircle size={15} />
            <h3>Help &amp; Legal</h3>
          </div>
          <LinkRow
            icon={FiHelpCircle}
            label="FAQ"
            desc="Browse common questions"
            href={appInfo?.faq_url}
            onNavigate={() => navigate("/faq")}
          />
          <LinkRow
            icon={FiInfo}
            label="Privacy Policy"
            desc="How we handle your data"
            href={appInfo?.privacy_url}
            onNavigate={() => navigate("/privacy-policy")}
          />
          <LinkRow
            icon={FiInfo}
            label="Terms & Conditions"
            desc="Terms of use for FleetManagementPro"
            href={appInfo?.terms_url}
            onNavigate={() => navigate("/terms-and-conditions")}
          />
        </div>

      </div>
      {showPhotoModal && profile?.photo_url && (
        <div className="stg-photo-modal-overlay" onClick={() => setShowPhotoModal(false)}>
          <div className="stg-photo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="stg-photo-modal-close" onClick={() => setShowPhotoModal(false)}>
              <FiX size={18} />
            </button>
            <img
              src={resolvePhotoUrl(profile.photo_url)}
              alt={profile.full_name}
              className="stg-photo-modal-img"
            />
            <p className="stg-photo-modal-name">{profile.full_name}</p>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function DashboardShell({ children }) {
  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <Header />
        <div className="stg-page">{children}</div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, badge }) {
  return (
    <div className="stg-info-row">
      <div className="stg-info-icon"><Icon size={14} /></div>
      <div className="stg-info-content">
        <span className="stg-info-label">{label}</span>
        <span className="stg-info-value">{value || "—"}</span>
      </div>
      {badge && <span className={`stg-badge stg-badge--${badge.type}`}>{badge.text}</span>}
    </div>
  );
}

function LinkRow({ icon: Icon, label, desc, href, onNavigate }) {
  const isExternal = href && href.startsWith("http");

  const handleClick = (e) => {
    if (isExternal) return;
    e.preventDefault();
    if (onNavigate) onNavigate();
  };

  return (
    <a
      className="stg-link-row"
      href={href || "#"}
      onClick={handleClick}
      target={isExternal ? "_blank" : "_self"}
      rel="noreferrer"
    >
      <div className="stg-info-icon"><Icon size={14} /></div>
      <div className="stg-info-content">
        <span className="stg-info-label">{label}</span>
        <span className="stg-info-value">{desc}</span>
      </div>
      <FiChevronRight size={14} className="stg-chevron" />
    </a>
  );
}